/* Tiny hyperscript helper · no framework, keeps render code declarative. */

type Child = Node | string | number | null | undefined | false;
type Attrs = Record<string, unknown>;

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null || v === false) continue;
    if (k === "class") el.className = String(v);
    else if (k === "html") el.innerHTML = String(v);
    else if (k.startsWith("on") && typeof v === "function") {
      el.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
    } else if (k === "dataset" && typeof v === "object") {
      Object.assign(el.dataset, v);
    } else if (v === true) {
      el.setAttribute(k, "");
    } else {
      el.setAttribute(k, String(v));
    }
  }
  for (const c of children.flat()) {
    if (c == null || c === false) continue;
    el.append(c instanceof Node ? c : document.createTextNode(String(c)));
  }
  return el;
}

export function clear(el: HTMLElement): void {
  el.replaceChildren();
}

/** Reveal `.fx` content injected AFTER the page's initial reveal pass.
 *  The per-page IntersectionObserver only knows the elements present at mount,
 *  so anything added later (lazy fetches, expanded panels) keeps the `.fx`
 *  starting style `opacity:0` forever — a blank gap. Call this on any subtree
 *  added later to animate its `.fx` bits in. */
export function reveal(el: HTMLElement): void {
  requestAnimationFrame(() => {
    if (el.classList.contains("fx")) el.classList.add("in");
    el.querySelectorAll(".fx").forEach((n) => n.classList.add("in"));
  });
}

/** Inline SVG icon set (stroked, currentColor). */
export const icon = (name: string, size = 18): SVGSVGElement => {
  const paths: Record<string, string> = {
    search: '<circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/>',
    arrow: '<path d="M5 12h14M13 6l6 6-6 6"/>',
    back: '<path d="M19 12H5M11 6l-6 6 6 6"/>',
    clock: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>',
    building: '<rect x="5" y="3" width="14" height="18" rx="1"/><path d="M9 7h2M9 11h2M9 15h2M13 7h2M13 11h2M13 15h2"/>',
    ship: '<path d="M3 14l2 6h14l2-6M5 14V8h14v6M9 8V5h6v3M12 2v3"/>',
    pin: '<path d="M12 21s7-6.5 7-12a7 7 0 1 0-14 0c0 5.5 7 12 7 12Z"/><circle cx="12" cy="9" r="2.5"/>',
    layers: '<path d="m12 3 9 5-9 5-9-5 9-5ZM3 13l9 5 9-5"/>',
    wave: '<path d="M2 12c2 0 2-3 4-3s2 3 4 3 2-3 4-3 2 3 4 3 2-3 4-3"/>',
    "chevron-down": '<path d="m6 9 6 6 6-6"/>',
    "alert-triangle": '<path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"/><path d="M12 9v4M12 17h.01"/>',
  };
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("width", String(size));
  svg.setAttribute("height", String(size));
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "1.7");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  svg.innerHTML = paths[name] ?? "";
  return svg;
};

export function eq(bars = 4): HTMLElement {
  const wrap = h("span", { class: "eq", "aria-hidden": "true" });
  for (let i = 0; i < bars; i++) wrap.append(h("span", { style: `--i:${i}` }));
  return wrap;
}
